/**
 * Report Data Aggregator
 *
 * Collects all computed data from every utility into a single structured
 * "report payload" object. This is the single source of truth consumed
 * by both the Insights pages and the Executive Report builder.
 * One computation, two presentations.
 *
 * This function does NOT compute anything new — it reorganizes existing
 * outputs into a consumption-friendly shape.
 */

/**
 * Aggregate all assessment data into a unified report payload.
 *
 * @param {Object} params - All computed data from the assessment pipeline
 * @returns {Object} Unified report payload organized by page/section
 */
export function aggregateReportData({
  assessment = {},
  client = {},
  scores = {},
  benchmarkData = {},
  alignments = [],
  gapAnalysis = {},
  tapsResults = {},
  lossScenarios = {},
  monteCarloResults = {},
  roadmap = {},
  enablementPlan = {},
  frameworkCoverage = {},
  insuranceAdequacy = {},
  dynamicCalibration = {},
  threatContextSummary = {},
  engagementContext = {},
  planBreakdown = {}
} = {}) {

  const overall = scores?.overall || {}
  const maturity = scores?.maturity || {}
  const riskAppetite = scores?.riskAppetite || {}

  return {
    metadata: {
      clientName: client.name || 'Client',
      industry: client.industry || 'Other',
      industrySubCategory: client.industrySubCategory || client.industry_sub_category || null,
      assessmentDate: assessment.completedAt || assessment.completed_at || null,
      assessmentTitle: assessment.title || 'Discovery Assessment',
      generatedAt: new Date().toISOString(),
      marketSegment: benchmarkData?.segmentId || 'mid-market',
      engagementContext: {
        primaryDriver: engagementContext.primaryDriver || null,
        timeline: engagementContext.timeline || null,
        outcomes: engagementContext.outcomes || [],
        urgencyLevel: engagementContext.urgencyLevel || null
      }
    },

    // Page: Program Overview
    programHealth: {
      overallScore: overall.score || 0,
      maturityLevel: overall.maturityLevel || { level: 1, label: 'Initial', color: '#C04A3D' },
      benchmark: {
        industry: benchmarkData?.benchmark || 0,
        gap: benchmarkData?.gap || 0,
        severity: benchmarkData?.severity?.label || 'Unknown',
        segmentAdjusted: benchmarkData?.segmentAdjustedBenchmark || benchmarkData?.benchmark || 0
      },
      strongestDomains: (overall.strongestCategories || []).slice(0, 3).map(c => ({ name: c.name, score: c.score })),
      weakestDomains: (overall.weakestCategories || []).slice(0, 3).map(c => ({ name: c.name, score: c.score })),
      riskAppetite: {
        average: riskAppetite.averageAppetite || 0,
        label: riskAppetite.averageLabel || 'Unknown'
      },
      categoryCount: overall.categoryCount || 0
    },

    // Page: Domain Analysis
    domainAnalysis: {
      categories: Object.entries(maturity?.categoryScores || {}).map(([id, cat]) => ({
        id,
        name: cat.name || id,
        score: cat.score || 0,
        maturityLevel: cat.maturityLevel || { level: 1, label: 'Initial' },
        questionCount: cat.questionCount || 0,
        answeredCount: cat.answeredCount || cat.questionCount || 0
      }))
    },

    // Page: Risk & Threats
    riskAndThreats: {
      tapsPriorities: (tapsResults?.categories || [])
        .sort((a, b) => (b.tapsScore || 0) - (a.tapsScore || 0))
        .slice(0, 5)
        .map(c => ({
          domain: c.domain,
          tapsScore: c.tapsScore,
          gapScore: c.gapScore,
          threatRelevance: c.threatRelevance,
          priorityLevel: c.priorityLevel
        })),
      threatProfile: {
        topActors: (threatContextSummary?.topThreatActors || []).slice(0, 4),
        topVectors: (threatContextSummary?.topAttackVectors || []).slice(0, 4),
        topAssets: (threatContextSummary?.topTargetAssets || []).slice(0, 4),
        profileName: threatContextSummary?.profile?.name || null,
        industryContext: threatContextSummary?.profile?.description || null
      },
      lossScenarios: {
        scenarios: (lossScenarios?.scenarios || []).map(s => ({
          scenarioId: s.scenarioId,
          name: s.scenarioName || s.name || s.shortName || s.scenarioId,
          probability: s.probability,
          grossLoss: s.grossLoss,
          netExposure: s.netExposure,
          ale: s.ale,
          severity: s.severity
        })),
        aggregateALE: lossScenarios?.scenarios?.reduce((sum, s) => sum + (s.ale || 0), 0) || 0,
        highestRisk: lossScenarios?.scenarios?.sort((a, b) => (b.ale || 0) - (a.ale || 0))?.[0]?.scenarioName || lossScenarios?.scenarios?.[0]?.name || null
      },
      monteCarlo: {
        p50: monteCarloResults?.aggregateDistribution?.percentiles?.p50 || 0,
        p75: monteCarloResults?.aggregateDistribution?.percentiles?.p75 || 0,
        p90: monteCarloResults?.aggregateDistribution?.percentiles?.p90 || 0,
        p95: monteCarloResults?.aggregateDistribution?.percentiles?.p95 || 0,
        p99: monteCarloResults?.aggregateDistribution?.percentiles?.p99 || 0,
        executiveSummary: monteCarloResults?.executiveSummary || null,
        exceedanceCurve: monteCarloResults?.exceedanceCurve || [],
        incidentFrequency: monteCarloResults?.incidentFrequency || {}
      },
      insurance: {
        adequacy: insuranceAdequacy?.overallAdequacy || 'Unknown',
        coverageScore: insuranceAdequacy?.coverageScore || 0,
        coverageLimit: insuranceAdequacy?.coverageLimit || 0,
        recommendedCoverage: insuranceAdequacy?.recommendedCoverage ?? null,
        recommendedCoverageRationale: insuranceAdequacy?.recommendedCoverageRationale || null,
        recommendedCoverageBindingAnchor: insuranceAdequacy?.recommendedCoverageBindingAnchor || null,
        gaps: insuranceAdequacy?.gaps || [],
        recommendations: insuranceAdequacy?.recommendations || [],
        scenarioAdequacy: insuranceAdequacy?.scenarioAdequacy || [],
        probabilityOfExceedingCoverage: insuranceAdequacy?.probabilityOfExceedingCoverage || 0,
        reserveRecommendation: insuranceAdequacy?.reserveRecommendation || null
      }
    },

    // Page: Regulatory Landscape
    regulatoryLandscape: {
      applicableFrameworks: client.regulatory_frameworks || client.regulatoryFrameworks || [],
      coverageAnalysis: frameworkCoverage || {},
      recentUpdates: [] // Populated by the component from regulatoryUpdates.json
    },

    // Page: Enablement Plan / Report section
    investmentPriorities: {
      quickWins: {
        thirtyDay: planBreakdown?.thirtyDay?.initiatives || [],
        sixtyDay: planBreakdown?.sixtyDay?.initiatives || [],
        ninetyDay: planBreakdown?.ninetyDay?.initiatives || []
      },
      strategic: planBreakdown?.strategic?.initiatives || [],
      longTerm: planBreakdown?.longTerm?.initiatives || [],
      totalInvestment: {
        min: (planBreakdown?.thirtyDay?.totalCost?.min || 0) + (planBreakdown?.sixtyDay?.totalCost?.min || 0) + (planBreakdown?.ninetyDay?.totalCost?.min || 0) + (planBreakdown?.strategic?.totalCost?.min || 0),
        max: (planBreakdown?.thirtyDay?.totalCost?.max || 0) + (planBreakdown?.sixtyDay?.totalCost?.max || 0) + (planBreakdown?.ninetyDay?.totalCost?.max || 0) + (planBreakdown?.strategic?.totalCost?.max || 0),
        formatted: null // Set below
      },
      totalCostOfInaction: tapsResults?.categories?.reduce((sum, c) => sum + (c.costOfInaction || 0), 0) || 0,
      roi: {
        threeYearROI: roadmap?.summary?.roi?.threeYearROI || 0,
        paybackMonths: roadmap?.summary?.roi?.paybackMonths || 0
      },
      executiveNarrative: enablementPlan?.narrative || null,
      totals: planBreakdown?.totals || {}
    }
  }
}

// Set formatted total investment after computation
// (done separately to avoid circular reference in the object literal)
